// src/components/CartDrawer.js - Fixed to respect minOrderQuantity
import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Button,
  Divider,
  Badge,
} from "@mui/material";
import {
  ShoppingCart,
  Close,
  Add,
  Remove,
  Delete,
  Star,
} from "@mui/icons-material";

const CartDrawer = ({
  cartDrawerOpen,
  handleCartDrawerClose,
  cart,
  getCartItemCount,
  getCartTotal,
  removeFromCart,
  updateCartQuantity,
  canAddMoreToCart,
  formatPrice,
  primaryBlue,
  glassStyles,
  cardGlassStyles,
  setShowCart,
  setCartDrawerOpen,
}) => {
  // Helper function to handle quantity changes respecting minOrderQuantity
  const handleQuantityChange = (type, id, change) => {
    const currentItem = cart[type].find((item) => item.id === id);
    if (!currentItem) return;

    const newQuantity = currentItem.quantity + change;
    const minQuantity = currentItem.minOrderQuantity || 1;

    if (newQuantity <= 0) {
      removeFromCart(type, id);
    } else if (newQuantity < minQuantity) {
      // Don't allow reducing below minimum order quantity
      return; // Do nothing if trying to go below minimum
    } else {
      if (type === "items") {
        const stockLimit = currentItem.stockQuantity || Infinity;
        const maxOrderLimit = currentItem.maxOrderQuantity || Infinity;
        const maxQuantity = Math.min(stockLimit, maxOrderLimit);
        if (newQuantity <= maxQuantity) {
          updateCartQuantity(type, id, newQuantity);
        }
      } else {
        updateCartQuantity(type, id, newQuantity);
      }
    }
  };

  // Helper function to check if decrease button should be disabled
  const canDecreaseQuantity = (item) => {
    const minQuantity = item.minOrderQuantity || 1;
    return item.quantity > minQuantity;
  };

  return (
    <Drawer
      anchor="right"
      open={cartDrawerOpen}
      onClose={handleCartDrawerClose}
      PaperProps={{
        sx: {
          width: 450,
          maxWidth: "90vw",
          ...glassStyles,
          bgcolor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(30px)",
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: `linear-gradient(135deg, ${primaryBlue[50]}, ${primaryBlue[100]})`,
          borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: primaryBlue[800] }}
        >
          Shopping Cart ({getCartItemCount()})
        </Typography>
        <IconButton
          onClick={handleCartDrawerClose}
          sx={{
            ...glassStyles,
            bgcolor: "rgba(255, 255, 255, 0.3)",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.5)" },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {getCartItemCount() === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <ShoppingCart
            sx={{
              fontSize: 80,
              mb: 2,
              color: primaryBlue[300],
              opacity: 0.7,
            }}
          />
          <Typography
            variant="h5"
            sx={{ color: primaryBlue[600], mb: 1, fontWeight: 600 }}
          >
            Your cart is empty
          </Typography>
          <Typography variant="body1" sx={{ color: primaryBlue[400] }}>
            Add some amazing items to get started!
          </Typography>
        </Box>
      ) : (
        <>
          <List sx={{ flexGrow: 1, overflow: "auto", p: 1 }}>
            {/* Packages in Cart */}
            {cart.packages.map((pkg) => (
              <ListItem
                key={pkg.id}
                sx={{
                  ...cardGlassStyles,
                  mb: 1,
                  bgcolor: "rgba(255, 255, 255, 0.6)",
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: primaryBlue[500],
                      border: `2px solid ${primaryBlue[200]}`,
                    }}
                  >
                    <Star />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      sx={{ fontWeight: 600, color: primaryBlue[800] }}
                    >
                      {pkg.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography sx={{ color: primaryBlue[600] }}>
                        {formatPrice(pkg.price)} × {pkg.quantity}
                      </Typography>
                      {(pkg.minOrderQuantity || 1) > 1 &&
                        pkg.quantity === (pkg.minOrderQuantity || 1) && (
                          <Typography
                            variant="caption"
                            sx={{ color: "warning.main", fontWeight: 500 }}
                          >
                            Minimum: {pkg.minOrderQuantity} items
                          </Typography>
                        )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange("packages", pkg.id, -1)
                      }
                      disabled={!canDecreaseQuantity(pkg)}
                      sx={{
                        bgcolor: canDecreaseQuantity(pkg)
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.1)",
                        "&:hover": {
                          bgcolor: canDecreaseQuantity(pkg)
                            ? "rgba(255, 255, 255, 0.9)"
                            : "rgba(0, 0, 0, 0.1)",
                        },
                        "&:disabled": {
                          color: "rgba(0, 0, 0, 0.3)",
                        },
                      }}
                    >
                      <Remove />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeFromCart("packages", pkg.id)}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.7)",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.9)",
                        },
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}

            {/* Items in Cart */}
            {cart.items.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  ...cardGlassStyles,
                  mb: 1,
                  bgcolor: "rgba(255, 255, 255, 0.6)",
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={item.imageUrl}
                    sx={{ border: `2px solid ${primaryBlue[200]}` }}
                  >
                    <ShoppingCart />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      sx={{ fontWeight: 600, color: primaryBlue[800] }}
                    >
                      {item.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography sx={{ color: primaryBlue[600] }}>
                        {formatPrice(item.sellingPrice)} × {item.quantity}
                      </Typography>
                      {(item.minOrderQuantity || 1) > 1 &&
                        item.quantity === (item.minOrderQuantity || 1) && (
                          <Typography
                            variant="caption"
                            sx={{ color: "warning.main", fontWeight: 500 }}
                          >
                            Minimum: {item.minOrderQuantity} items
                          </Typography>
                        )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange("items", item.id, -1)}
                      disabled={!canDecreaseQuantity(item)}
                      sx={{
                        bgcolor: canDecreaseQuantity(item)
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.1)",
                        "&:hover": {
                          bgcolor: canDecreaseQuantity(item)
                            ? "rgba(255, 255, 255, 0.9)"
                            : "rgba(0, 0, 0, 0.1)",
                        },
                        "&:disabled": {
                          color: "rgba(0, 0, 0, 0.3)",
                        },
                      }}
                    >
                      <Remove />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange("items", item.id, 1)}
                      disabled={!canAddMoreToCart(item, item.quantity)}
                      sx={{
                        bgcolor: canAddMoreToCart(item, item.quantity)
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.1)",
                        "&:hover": {
                          bgcolor: canAddMoreToCart(item, item.quantity)
                            ? "rgba(255, 255, 255, 0.9)"
                            : "rgba(0, 0, 0, 0.1)",
                        },
                        "&:disabled": {
                          color: "rgba(0, 0, 0, 0.3)",
                        },
                      }}
                    >
                      <Add />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeFromCart("items", item.id)}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.7)",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.9)",
                        },
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${primaryBlue[50]}, ${primaryBlue[100]})`,
              borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: primaryBlue[800] }}
              >
                Total:
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {formatPrice(getCartTotal())}
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => {
                setShowCart(true);
                setCartDrawerOpen(false);
              }}
              sx={{
                mb: 2,
                py: 1.5,
                background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontWeight: "bold",
                fontSize: "1.1rem",
                boxShadow: "0 8px 32px rgba(0, 114, 206, 0.3)",
                "&:hover": {
                  background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0, 114, 206, 0.4)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              Proceed to Checkout
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCartDrawerClose}
              sx={{
                py: 1.5,
                borderColor: primaryBlue[300],
                color: primaryBlue[600],
                borderRadius: "12px",
                fontWeight: 600,
                bgcolor: "rgba(255, 255, 255, 0.7)",
                "&:hover": {
                  borderColor: primaryBlue[500],
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              Continue Shopping
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default CartDrawer;
